import { useModal } from "@components/Modal";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SourceDocumentViewer } from "./SourceDocumentViewer";

export default function SourceCarousel({
  courseId,
  citations,
}: {
  courseId: string;
  citations: string[];
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation("chatPage");

  const { Modal: SourceModal, openModal: openSourceModal } = useModal();

  const prevSlide = (e: React.UIEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const nextSlide = (e: React.UIEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setCurrentIndex((prev) => Math.min(prev + 1, citations.length - 1));
  };

  const handleImageClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { left, width } = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - left;
    if (clickX < width / 2) {
      prevSlide(e);
    } else {
      nextSlide(e);
    }
  };

  const [isFirstOpened, setIsFirstOpened] = useState(false);

  return (
    <div className="mb-4">
      <button
        onClick={() => {
          setIsFirstOpened(true);
          openSourceModal();
        }}
        type="button"
        className="btn-primary btn btn-sm text-white"
      >
        {t("showSources")} ({citations.length})
      </button>

      <SourceModal isWider={true} title="">
        <button
          type="button"
          onKeyDown={prevSlide}
          onKeyUp={nextSlide}
          onClick={handleImageClick}
          className="relative flex h-full w-full cursor-pointer items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label="Navigate source carousel"
        >
          {citations.map((docId, index) => (
            <div
              key={docId}
              className={`absolute top-0 left-0 h-full w-full transition-opacity duration-300 ${
                index === currentIndex
                  ? "relative opacity-100"
                  : "pointer-events-none opacity-0"
              }`}
            >
              <SourceDocumentViewer
                courseId={courseId}
                documentId={docId}
                load={isFirstOpened}
              />
            </div>
          ))}
          {currentIndex > 0 && (
            <button
              type="button"
              onClick={prevSlide}
              className="-translate-y-1/2 absolute top-1/2 left-0 transform rounded-full bg-gray-700/50 p-2 hover:bg-gray-700/75 focus:outline-none"
            >
              <ArrowLeftIcon className="w-5" />
            </button>
          )}
          {currentIndex < citations.length - 1 && (
            <button
              type="button"
              onClick={nextSlide}
              className="-translate-y-1/2 absolute top-1/2 right-0 transform rounded-full bg-gray-700/50 p-2 hover:bg-gray-700/75 focus:outline-none"
            >
              <ArrowRightIcon className="w-5" />
            </button>
          )}
        </button>
      </SourceModal>
    </div>
  );
}
